import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import { type MouseEvent, useMemo, useState } from 'react'
import {
  type SaleAmendment,
  useCancelSaleAmendmentMutation,
  useGetSaleAmendmentsQuery,
  useSeparateSaleAmendmentContractMutation,
} from '../../store/api/saleAmendmentsApi'
import type { SaleContractPayload } from '../../store/api/saleContractApi'
import ContractFormDialog from './ContractFormDialog'
import SaleAmendmentFormDialog from './SaleAmendmentFormDialog'
import SaleAmendmentPrintDialog from './SaleAmendmentPrintDialog'
import { usePagePermission } from '../../hooks/usePagePermission'

const ALL_ROWS_PAGE_SIZE = 1_000_000

const filterBarBtnSx: SxProps<Theme> = (theme) => ({
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  textTransform: 'none',
  fontSize: '0.7rem',
  lineHeight: 1.1,
  '&.MuiButton-outlined': {
    borderColor: alpha(theme.palette.text.primary, 0.14),
    '&:hover': {
      borderColor: alpha(theme.palette.text.primary, 0.24),
      backgroundColor: alpha(theme.palette.text.primary, 0.04),
    },
  },
})

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

type ColumnKind = 'text' | 'date' | 'number'
type ColumnDef = { key: keyof SaleAmendment; label: string; kind?: ColumnKind; align?: 'right' }

// §2.4 / §5: exact column order from Access frmContractSalesQryAnnex; id shown per product request (Access had it hidden)
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'id', label: 'ID', kind: 'number', align: 'right' },
  { key: 'amendmentNo', label: 'Amendment No.' },
  { key: 'amendmentDate', label: 'Amendment Date.', kind: 'date' },
  { key: 'contractNo', label: 'Contract Ref' },
  { key: 'clientCode', label: 'Supplier Code' },
  { key: 'clientName', label: 'Supplier' },
  { key: 'contentBeforeChange', label: 'Before change' },
  { key: 'contentAfterChange', label: 'After change' },
  { key: 'note', label: 'Note' },
  { key: 'price', label: 'Price', kind: 'number', align: 'right' },
  { key: 'quantity', label: 'Quantity', kind: 'number', align: 'right' },
  { key: 'dateUpdate', label: 'Updated Date', kind: 'date' },
  { key: 'personUpdate', label: 'Updated Person' },
]

const NON_SUM_TOTAL_KEYS = new Set<keyof SaleAmendment>(['id'])
const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  return `${day}-${month}-${year}`
}

const formatAccessNumber = (value: number): string => {
  if (Number.isInteger(value)) return value.toLocaleString('en-US')
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const sumNumericColumn = (rows: SaleAmendment[], key: keyof SaleAmendment): number | null => {
  let total = 0
  let count = 0
  for (const row of rows) {
    const value = row[key]
    if (typeof value !== 'number' || !Number.isFinite(value)) continue
    total += value
    count += 1
  }
  return count > 0 ? total : null
}

const SEARCH_KEYS: Array<keyof SaleAmendment> = [
  'amendmentNo', 'contractNo', 'clientCode', 'clientName',
  'contentBeforeChange', 'contentAfterChange', 'note',
]

const rowMatchesSearch = (row: SaleAmendment, term: string): boolean => {
  const needle = term.trim().toLowerCase()
  if (!needle) return true
  return SEARCH_KEYS.some((k) => {
    const value = row[k]
    if (value == null) return false
    return String(value).toLowerCase().includes(needle)
  })
}

export default function SaleAmendmentListView() {
  const { canEdit } = usePagePermission()
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterPopover, setFilterPopover] = useState<{ anchor: HTMLElement } | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [formMode, setFormMode] = useState<'new' | 'edit'>('new')
  const [printLang, setPrintLang] = useState<'en' | 'vn' | null>(null)
  const [contractDraft, setContractDraft] = useState<SaleContractPayload | null>(null)
  const [contractDialogOpen, setContractDialogOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const { data: amendmentList, isLoading, isFetching, error } = useGetSaleAmendmentsQuery({
    page: 1,
    pageSize: ALL_ROWS_PAGE_SIZE,
  })
  const [cancelAmendment, { isLoading: isCancelling }] = useCancelSaleAmendmentMutation()
  const [separateContract, { isLoading: isSeparating }] = useSeparateSaleAmendmentContractMutation()

  const baseRows = useMemo(() => amendmentList?.items ?? [], [amendmentList])
  const filteredRows = useMemo(() => baseRows.filter((row) => rowMatchesSearch(row, searchTerm)), [baseRows, searchTerm])
  const rows = useMemo(() => {
    if (rowsPerPage === -1) return filteredRows
    const start = page * rowsPerPage
    return filteredRows.slice(start, start + rowsPerPage)
  }, [filteredRows, page, rowsPerPage])
  const selectedRow = useMemo(
    () => filteredRows.find((row) => row.amendmentNo === selectedKey) ?? null,
    [filteredRows, selectedKey],
  )
  const totalRowValues = useMemo(
    () =>
      new Map(
        TABLE_COLUMNS.filter((col) => col.kind === 'number' && !NON_SUM_TOTAL_KEYS.has(col.key)).map((col) => [
          col.key,
          sumNumericColumn(filteredRows, col.key),
        ]),
      ),
    [filteredRows],
  )
  const idRangeText = useMemo(() => {
    const ids = rows.map((r) => Number(r.id)).filter(Number.isFinite)
    if (!ids.length) return 'ID: —'
    return `ID: ${Math.min(...ids)}-${Math.max(...ids)}`
  }, [rows])
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}`

  const openForm = (mode: 'new' | 'edit') => {
    if (mode === 'edit' && !selectedRow) {
      window.alert('Please choose a record!')
      return
    }
    setFormMode(mode)
    setFormOpen(true)
  }

  const handleDeleteClick = () => {
    if (!selectedRow) {
      window.alert('Please choose a record!')
      return
    }
    setDeleteConfirmOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!selectedRow) return
    setDeleteConfirmOpen(false)
    try {
      await cancelAmendment(selectedRow.amendmentNo).unwrap()
      setSelectedKey(null)
    } catch {
      setActionError('Failed to delete amendment.')
    }
  }

  const handleContractSeparation = async () => {
    if (!selectedRow) {
      window.alert('Please choose a record!')
      return
    }
    try {
      const result = await separateContract(selectedRow.amendmentNo).unwrap()
      setContractDraft(result.draft)
      setContractDialogOpen(true)
    } catch {
      setActionError('Failed to prefill separated contract.')
    }
  }

  const cellValue = (row: SaleAmendment, col: ColumnDef): string => {
    const val = row[col.key]
    if (val == null) return ''
    if (col.kind === 'date') return formatAccessDate(val)
    if (col.kind === 'number' && typeof val === 'number') return formatAccessNumber(val)
    if (typeof val === 'number') return formatAccessNumber(val)
    return String(val)
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" flexWrap="wrap" useFlexGap justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
        <Stack direction="row" spacing={0.375} flexWrap="wrap" useFlexGap alignItems="center">
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>
            Filter
          </Typography>
          <Button
            size="small"
            variant={searchTerm.trim() ? 'contained' : 'outlined'}
            color={searchTerm.trim() ? 'primary' : 'inherit'}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e: MouseEvent<HTMLElement>) => setFilterPopover({ anchor: e.currentTarget })}
            sx={filterBarBtnSx}
          >
            Search
          </Button>
          {searchTerm.trim() && (
            <Button size="small" variant="text" color="secondary" onClick={() => setSearchTerm('')} sx={filterBarBtnSx}>
              Clear All
            </Button>
          )}
        </Stack>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
          <Button variant="contained" size="small" color="success" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={() => openForm('new')}>
            New
          </Button>
          <Button variant="outlined" size="small" color="warning" sx={actionToolbarBtnSx} disabled={!selectedRow || !canEdit} onClick={() => openForm('edit')}>
            Edit
          </Button>
          <Button variant="outlined" size="small" color="error" sx={actionToolbarBtnSx} disabled={!selectedRow || isCancelling || !canEdit} onClick={handleDeleteClick}>
            Delete
          </Button>
          <Button variant="outlined" size="small" sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }} disabled={!selectedRow} onClick={() => setPrintLang('en')}>
            Print EN
          </Button>
          <Button variant="outlined" size="small" sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }} disabled={!selectedRow} onClick={() => setPrintLang('vn')}>
            Print VN
          </Button>
          <Button variant="outlined" size="small" sx={actionToolbarBtnSx} disabled={!selectedRow || isSeparating || !canEdit} onClick={handleContractSeparation}>
            Contract Separation
          </Button>
        </Stack>
      </Stack>
      <Divider sx={{ mb: 0.5 }} />
      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Failed to load sale amendments.
        </Alert>
      ) : null}
      {actionError ? (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      ) : null}
      <Box className="app-dataframe">
        <Box className="app-dataframe-toolbar" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap', minHeight: 30, py: 0.125 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}>
              Total: {filteredRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}>
              Loaded: {baseRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}>
              {idRangeText}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={{
                flexShrink: 0,
                '& .MuiTablePagination-toolbar': { minHeight: 32, px: 0.5 },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.72rem' },
                '& .MuiTablePagination-select': { fontSize: '0.72rem' },
                '& .MuiTablePagination-actions': { transform: 'scale(0.92)', transformOrigin: 'center' },
              }}
            />
          </Stack>
        </Box>
        <Popover
          open={filterPopover !== null}
          anchorEl={filterPopover?.anchor ?? null}
          onClose={() => setFilterPopover(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ sx: { p: 1.5, minWidth: 260 } }}
        >
          <Stack spacing={1}>
            <TextField
              size="small"
              fullWidth
              autoFocus
              label="Search amendments"
              placeholder="Amendment no, contract no, client..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                setPage(0)
              }}
            />
            <Button size="small" variant="text" onClick={() => { setSearchTerm(''); setPage(0); setFilterPopover(null) }}>
              Clear Search
            </Button>
          </Stack>
        </Popover>
        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          <TableVirtuoso
            key={gridKey}
            style={{ height: '100%' }}
            data={rows}
            fixedHeaderContent={() => (
              <TableRow>
                {TABLE_COLUMNS.map((col) => (
                  <TableCell key={col.key} align={col.align} sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'background.default' }}>
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            )}
            fixedFooterContent={() => (
              <TableRow>
                {TABLE_COLUMNS.map((col, index) => {
                  const total = totalRowValues.get(col.key)
                  return (
                    <TableCell key={col.key} align={col.align} sx={{ fontWeight: 700, whiteSpace: 'nowrap', py: 0.5, bgcolor: 'background.paper', borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
                      {index === 0 ? 'Total' : total == null ? '' : formatAccessNumber(total)}
                    </TableCell>
                  )
                })}
              </TableRow>
            )}
            itemContent={(_: number, row: SaleAmendment) =>
              TABLE_COLUMNS.map((col) => {
                const val = cellValue(row, col)
                return (
                  <TableCell key={col.key} align={col.align} sx={{ whiteSpace: 'nowrap', py: 0.5 }}>
                    <Box component="span" className="app-table-cell-text" title={val || '-'} sx={{ display: 'inline-block', width: '100%' }}>
                      {val || '-'}
                    </Box>
                  </TableCell>
                )
              })
            }
            components={{
              Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: 3000, width: '100%' }} />,
              TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
              TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
              TableRow: (props: any) => {
                const idx = Number((props as any)['data-item-index'])
                const rowKey = Number.isFinite(idx) ? rows[idx]?.amendmentNo : null
                return (
                  <TableRow
                    {...props}
                    hover
                    selected={rowKey != null && rowKey === selectedKey}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      if (rowKey != null) setSelectedKey(rowKey)
                    }}
                  />
                )
              },
            }}
          />
        </Box>
      </Box>
      <SaleAmendmentFormDialog
        open={formOpen}
        mode={formMode}
        amendmentNo={selectedRow?.amendmentNo}
        onClose={() => setFormOpen(false)}
      />
      <SaleAmendmentPrintDialog
        open={printLang !== null}
        language={printLang ?? 'en'}
        amendment={selectedRow}
        onClose={() => setPrintLang(null)}
      />
      <ContractFormDialog
        open={contractDialogOpen}
        onClose={() => setContractDialogOpen(false)}
        mode="new"
        initialDraft={contractDraft}
      />

      {/* ── Delete confirmation dialog ── */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', color: 'error.main' }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Do you want to delete this annex no =&nbsp;
            <strong>{selectedRow?.amendmentNo}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5, gap: 0.5 }}>
          <Button size="small" variant="outlined" onClick={() => setDeleteConfirmOpen(false)}>
            Cancel
          </Button>
          <Button size="small" variant="contained" color="error" onClick={handleDeleteConfirm} disabled={isCancelling}>
            {isCancelling ? <CircularProgress size={14} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
